/**
 * analytics.js — client-side funnel event buffer + sender.
 *
 * Design (approved in PRD 19 Apr 2026):
 *  - Persistent anonymous_id in localStorage, survives logout/login.
 *  - Events buffered in memory and flushed in batches of ≤10 every 5s
 *    or when the buffer fills, whichever comes first.
 *  - On pagehide/visibilitychange we flush via navigator.sendBeacon so
 *    events don't get lost when the user navigates to Stripe checkout.
 *  - Only events in CLIENT_EVENT_NAMES are accepted by the server; anything
 *    else is silently dropped server-side. We don't enforce on the client
 *    so that a bad event name here never blocks a flush.
 *  - identify() is called on login/register to map anonymous_id → user_id.
 *
 * Usage:
 *   import { trackEvent, identify, getAnonymousId } from '../lib/analytics';
 *   trackEvent('scope_plan_uploaded', { draft_id, properties: { floor_id } });
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'sow_anon_id';
const SESSION_KEY = 'sow_session_id';
const MAX_BATCH = 10;
const FLUSH_INTERVAL_MS = 5000;

// ── Anonymous ID (persistent across sessions) ───────────────────────
function genId() {
  // RFC4122 v4 via crypto.getRandomValues (widely supported; no uuid dep)
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  // Fallback for older browsers
  return 'anon_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function getAnonymousId() {
  try {
    let id = localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = genId();
      localStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  } catch {
    // localStorage disabled → generate ephemeral id per page load
    return genId();
  }
}

export function getSessionId() {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = genId();
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return genId();
  }
}

// ── Buffer + batched send ───────────────────────────────────────────
const buffer = [];
let flushTimer = null;

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
}

async function flush() {
  clearTimeout(flushTimer);
  flushTimer = null;
  if (buffer.length === 0) return;
  const batch = buffer.splice(0, buffer.length);
  try {
    await axios.post(`${API}/analytics/track`, { events: batch });
  } catch {
    // Silent — analytics are best-effort. Don't re-buffer to avoid infinite growth.
  }
}

function flushBeacon() {
  if (buffer.length === 0) return;
  const batch = buffer.splice(0, buffer.length);
  try {
    const body = JSON.stringify({ events: batch });
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(`${API}/analytics/track`, blob);
    } else {
      // sync XHR fallback (rare path — only pre-modern browsers)
      axios.post(`${API}/analytics/track`, { events: batch });
    }
  } catch { /* ignored */ }
}

// Pagehide/visibilitychange — flush before the browser tears down the tab
if (typeof window !== 'undefined') {
  const handler = () => { if (document.visibilityState === 'hidden') flushBeacon(); };
  window.addEventListener('visibilitychange', handler);
  window.addEventListener('pagehide', flushBeacon);
}

// ── Public API ──────────────────────────────────────────────────────
export function trackEvent(event_name, extra = {}) {
  if (!event_name) return;
  const payload = {
    event_name,
    anonymous_id: getAnonymousId(),
    session_id: getSessionId(),
    path: extra.path || (typeof window !== 'undefined' ? window.location.pathname : null),
    draft_id: extra.draft_id || null,
    project_id: extra.project_id || null,
    properties: extra.properties || {},
    referrer: typeof document !== 'undefined' ? document.referrer : '',
  };
  buffer.push(payload);
  if (buffer.length >= MAX_BATCH) {
    flush();
  } else {
    scheduleFlush();
  }
}

export async function identify(user_id) {
  // Server doesn't actually need user_id from the body — it reads the auth cookie.
  // We just need to tell it: "here's the anonymous_id I've been using".
  try {
    await axios.post(`${API}/analytics/identify`, {
      anonymous_id: getAnonymousId(),
    });
  } catch { /* ignored */ }
}

export function flushNow() { return flush(); }
